<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Р9.3: создание/редактирование пары «вопрос-ответ» в qa-базе знаний
// (без выбора агента — запись живёт в конкретной базе).
const props = defineProps({
  knowledgeBaseId: {
    type: Number,
    required: true,
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedResponse: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'success']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const i18nKey = computed(
  () => `ASSISTANT.KNOWLEDGE_BASES.QA.${props.type.toUpperCase()}`
);

const uiFlags = useMapGetter('assistantResponses/getUIFlags');
const isLoading = computed(
  () => uiFlags.value.creatingItem || uiFlags.value.updatingItem
);

const state = reactive({
  question: '',
  answer: '',
});

const validationRules = {
  question: { required, minLength: minLength(1) },
  answer: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const formErrors = computed(() => ({
  question: v$.value.question.$error
    ? t('ASSISTANT.KNOWLEDGE_BASES.QA.FORM.QUESTION.ERROR')
    : '',
  answer: v$.value.answer.$error
    ? t('ASSISTANT.KNOWLEDGE_BASES.QA.FORM.ANSWER.ERROR')
    : '',
}));

watch(
  () => props.selectedResponse,
  response => {
    if (props.type !== 'edit' || !response) return;

    Object.assign(state, {
      question: response.question || '',
      answer: response.answer || '',
    });
  },
  { immediate: true }
);

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    if (props.type === 'edit') {
      await store.dispatch('assistantResponses/update', {
        id: props.selectedResponse.id,
        question: state.question,
        answer: state.answer,
      });
    } else {
      await store.dispatch('assistantResponses/create', {
        knowledge_base_id: props.knowledgeBaseId,
        question: state.question,
        answer: state.answer,
      });
    }
    emit('success');
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.message ||
      parseAPIErrorResponse(error) ||
      t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleClose = () => emit('close');
const handleCancel = () => dialogRef.value.close();

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t(`${i18nKey}.TITLE`)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <Input
        v-model="state.question"
        :label="$t('ASSISTANT.KNOWLEDGE_BASES.QA.FORM.QUESTION.LABEL')"
        :placeholder="
          $t('ASSISTANT.KNOWLEDGE_BASES.QA.FORM.QUESTION.PLACEHOLDER')
        "
        :message="formErrors.question"
        :message-type="formErrors.question ? 'error' : 'info'"
      />
      <Editor
        v-model="state.answer"
        :label="$t('ASSISTANT.KNOWLEDGE_BASES.QA.FORM.ANSWER.LABEL')"
        :placeholder="
          $t('ASSISTANT.KNOWLEDGE_BASES.QA.FORM.ANSWER.PLACEHOLDER')
        "
        :message="formErrors.answer"
        :max-length="10000"
        :message-type="formErrors.answer ? 'error' : 'info'"
      />
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="$t('ASSISTANT.FORM.CANCEL')"
          class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
          @click="handleCancel"
        />
        <Button
          type="submit"
          :label="$t(`ASSISTANT.FORM.${type.toUpperCase()}`)"
          class="w-full"
          :is-loading="isLoading"
          :disabled="isLoading"
        />
      </div>
    </form>
    <template #footer />
  </Dialog>
</template>
